"use client";

import type { SlotComponent } from "@puckeditor/core";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { PuckBlockProps } from "../types";

/** A movable envelope around individually editable supporting blocks. */
export function SupportingContent({
  title,
  layout,
  appearance,
  desktopSidebar = false,
  content: Content,
}: Omit<PuckBlockProps["SupportingContent"], "content"> & { content: SlotComponent }) {
  const sidebar = appearance === "sidebar";
  return (
    <Box
      data-testid="landing-page-supporting-content"
      data-appearance={appearance}
      data-layout={layout}
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        ...(sidebar
          ? { p: 0, m: 0, border: 0, bgcolor: "transparent" }
          : {
              mt: 4,
              px: 2.5,
              pt: 6,
              pb: 4.5,
              borderTop: 1,
              borderColor: "dream.borderColor",
              bgcolor: "dream.grayLighter",
            }),
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: sidebar ? "none" : layout === "vertical" ? 600 : 900,
          mx: "auto",
        }}
      >
        {title && (
          <Typography
            component={sidebar ? "h2" : "h3"}
            sx={{
              color: sidebar ? "primary.main" : "dream.grayDark",
              fontSize: sidebar ? "1.5rem" : "1.05rem",
              fontWeight: 700,
              lineHeight: sidebar ? 1.3 : 1.4,
              mb: 2.5,
              textAlign: sidebar ? "left" : "center",
            }}
          >
            {title}
          </Typography>
        )}
        <Box
          sx={{
            "& > div": {
              width: "100%",
              display: layout === "grid" ? "grid" : "flex",
              gap: sidebar ? "20px" : "12px",
              "& > *": { width: "100%", minWidth: 0 },
              ...(layout === "vertical"
                ? { flexDirection: "column", alignItems: sidebar ? "stretch" : "center" }
                : layout === "horizontal"
                  ? {
                      flexDirection: "row",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      "& > *": { width: "100%", flex: "1 1 0", minWidth: 180 },
                      "& > [data-supporting-block]": { minWidth: 0 },
                    }
                  : {
                      gridTemplateColumns: sidebar ? "1fr" : "repeat(2, 1fr)",
                      alignItems: "start",
                      ...(!sidebar ? { justifyItems: "center" } : {}),
                    }),
              ...(sidebar && layout === "grid"
                ? {
                    "@media (min-width: 600px)": {
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    },
                  }
                : {}),
              ...(desktopSidebar
                ? {
                    "@media (min-width: 600px)": {
                      ...(layout === "horizontal"
                        ? { "& > *, & > [data-supporting-block]": { minWidth: 160 } }
                        : {}),
                      ...(sidebar && layout === "grid"
                        ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
                        : {}),
                    },
                    "@media (min-width: 800px)": {
                      gap: "20px",
                      alignItems: "stretch",
                      ...(layout === "horizontal"
                        ? {
                            overflowWrap: "anywhere",
                            "& > *, & > [data-supporting-block]": { minWidth: 72 },
                          }
                        : {}),
                    },
                  }
                : {}),
            },
          }}
        >
          <Content minEmptyHeight={100} />
        </Box>
      </Box>
    </Box>
  );
}
